import { getAuthHeaders } from '@/lib/api-utils';
import { ApiResponse } from '@/services/api-types';
import {
    handleApiResponseStatusCode,
    handleHttpStatusCode,
} from '@/lib/error-handler';
import { createClientLogger } from '@/lib/logger/client-logger';
import { trimObjectStrings } from '@/lib/common-utils';
import { isApiLogEnabled } from '@/lib/logger/config';

const logger = createClientLogger('api-factory');
// import { ApiMethodParams, ApiMethodReturns } from './api-types';

/**
 * API工厂函数，用于统一处理API请求
 * 简化客户端调用统一API的方式
 * 支持环境变量形式: api.post("NEXT_PUBLIC_API_xxx") 或简化形式: api.post("xxx")
 * 一下代码利用了TypeScript的函数重载机制
 */
export class ApiFactory {
    /**
     * 发送API请求（类型安全版本）
     * @param method API方法名
     * @param data 请求数据
     * @param options 请求选项
     * @returns Promise<ApiResponse<ApiMethodReturns[K]>>
     */
    // static async request<K extends keyof ApiMethodParams>(
    //     method: K,
    //     data?: ApiMethodParams[K],
    //     options?: RequestInit
    // ): Promise<ApiResponse<ApiMethodReturns[K]>>;

    /**
     * 发送通用API请求（支持未在类型映射中定义的API）
     * @param method API方法名
     * @param data 请求数据
     * @param options 请求选项
     * @returns Promise<ApiResponse<T>>
     */
    static async request<T, D = undefined>(
        method: string,
        data?: D,
        options?: RequestInit,
    ): Promise<ApiResponse<T>>;

    static async request<T, D>(
        method: string,
        data?: D,
        options?: RequestInit,
    ): Promise<ApiResponse<T>> {
        try {
            if (isApiLogEnabled()) {
                logger.info('API请求开始', {
                    method,
                    data: data ? JSON.stringify(data) : null,
                });
            }

            if (typeof window === 'undefined') {
                throw new Error('非客户端环境，无法进行api请求');
            }
            // 构建请求URL
            const url = new URL('/api', window.location.origin);

            url.searchParams.set('method', method);

            // 获取认证头部
            const authHeaders = getAuthHeaders();

            // 自动去除字符串前后空格
            const trimmedData = data ? trimObjectStrings(data) : undefined;

            // 构建请求配置
            const requestConfig: RequestInit = {
                method: 'POST',
                headers: {
                    ...(authHeaders || {}),
                    ...(options?.headers || {}),
                },
                body: trimmedData ? JSON.stringify(trimmedData) : undefined,
                ...options,
            };

            // 发送请求
            const response = await fetch(url.toString(), requestConfig);

            if (isApiLogEnabled()) {
                logger.info('API响应接收', {
                    method,
                    status: response.status,
                    statusText: response.statusText,
                });
            }

            const shouldAbortHttp = handleHttpStatusCode(response, 'API请求');
            if (shouldAbortHttp) {
                logger.error('HTTP状态码错误', {
                    method,
                    status: response.status,
                    statusText: response.statusText,
                });
                return {
                    success: false,
                    message: `HTTP错误: ${response.status} ${response.statusText}`,
                    result: {} as T,
                };
            }

            const result = (await response.json()) as ApiResponse<T>;

            if (isApiLogEnabled()) {
                logger.info('API响应数据', {
                    method,
                    statusCode: result.statusCode,
                    success: result.success,
                    message: result.message,
                });
            }

            // 使用通用函数处理API响应状态码
            const shouldAbort = handleApiResponseStatusCode(result);
            if (shouldAbort) {
                if (isApiLogEnabled()) {
                    logger.warn('API响应状态码被拦截', {
                        method,
                        statusCode: result.statusCode,
                        message: result.message,
                    });
                }
                return {
                    success: false,
                    message: result.message,
                    result: {} as T,
                };
            }

            return result;
        } catch (error) {
            if (isApiLogEnabled()) {
                logger.error('API请求异常', {
                    method,
                    error: error instanceof Error ? error.message : '未知错误',
                });
            }
            return {
                success: false,
                message: error instanceof Error ? error.message : '未知错误',
                result: {} as T,
            };
        }
    }

    /**
     * 发送文件上传请求
     * @param method API方法名
     * @param formData 要上传的FormData对象
     * @param options 请求选项
     * @returns Promise<ApiResponse<T>>
     */
    static async fileRequest<T>(
        method: string,
        formData: FormData,
        options?: RequestInit,
    ): Promise<ApiResponse<T>> {
        try {
            const fileCount = formData.getAll('file')?.length || 0;
            logger.info('文件上传请求开始', {
                method,
                fileCount,
                fileName:
                    formData.get('file') instanceof File
                        ? (formData.get('file') as File).name
                        : 'unknown',
            });
            if (typeof window === 'undefined') {
                throw new Error('非客户端环境，无法进行文件上传');
            }

            // 构建请求URL
            const url = new URL('/api/upload', window.location.origin);
            url.searchParams.set('method', method);

            // 获取认证头部
            const authHeaders = getAuthHeaders();

            // 构建请求配置
            const requestConfig: RequestInit = {
                method: 'POST',
                headers: {
                    ...(authHeaders || {}),
                    // 注意：不要设置Content-Type，让浏览器自动处理multipart/form-data边界
                },
                body: formData,
                ...options,
            };

            // 删除Content-Type头部以避免边界问题
            if (requestConfig.headers) {
                if (requestConfig.headers instanceof Headers) {
                    requestConfig.headers.delete('Content-Type');
                } else if (Array.isArray(requestConfig.headers)) {
                    // 对于字符串数组形式的headers，过滤掉Content-Type
                    requestConfig.headers = requestConfig.headers.filter(
                        ([key]) => key.toLowerCase() !== 'content-type',
                    );
                } else {
                    // 对于普通对象形式的headers
                    delete requestConfig.headers['Content-Type'];
                }
            }

            // 发送请求
            const response = await fetch(url.toString(), requestConfig);

            logger.info('文件上传响应接收', {
                method,
                status: response.status,
                statusText: response.statusText,
            });

            const shouldAbortHttp = handleHttpStatusCode(response, '文件上传');
            if (shouldAbortHttp) {
                logger.error('文件上传HTTP状态码错误', {
                    method,
                    status: response.status,
                    statusText: response.statusText,
                });
                return {
                    success: false,
                    message: `文件上传HTTP错误: ${response.status} ${response.statusText}`,
                    result: {} as T,
                };
            }

            const result = (await response.json()) as ApiResponse<T>;

            logger.info('文件上传响应数据', {
                method,
                statusCode: result.statusCode,
                success: result.success,
                message: result.message,
            });

            return result;
        } catch (error) {
            logger.error('文件上传请求异常', {
                method,
                error: error instanceof Error ? error.message : '未知错误',
                stack: error instanceof Error ? error.stack : undefined,
            });
            return {
                success: false,
                message:
                    error instanceof Error ? error.message : '文件上传失败',
                result: {} as T,
            };
        }
    }
}

// 导出常用API方法的便捷函数
export const api = {
    request: ApiFactory.request,
    fileRequest: ApiFactory.fileRequest,
};
